import { get, post, json, METRIC_KEYS } from './apiClient';

export async function incrementMetric(
  userId: string,
  metric: string,
  opts?: { src?: string; utm_source?: string; utm_medium?: string; utm_campaign?: string }
) {
  if (!userId || (!METRIC_KEYS.has(metric) && !String(metric).endsWith('Clicks'))) {
    return { ok: false, skipped: true } as any;
  }
  // Auto-capture current page UTM/src if not explicitly provided
  try {
    const w: any = typeof window !== 'undefined' ? window : null;
    if (w && (!opts || (!opts.utm_source && !opts.src && !opts.utm_medium && !opts.utm_campaign))) {
      const qs = new URLSearchParams(String(w.location?.search || ''));
      const utm_source = qs.get('utm_source') || undefined;
      const utm_medium = qs.get('utm_medium') || undefined;
      const utm_campaign = qs.get('utm_campaign') || undefined;
      const src = qs.get('src') || undefined;
      if (utm_source || utm_medium || utm_campaign || src) {
        opts = { ...(opts || {}), utm_source, utm_medium, utm_campaign, src };
      }
    }
  } catch {}
  const qp = (() => {
    const q: string[] = [];
    const add = (k: string, v?: string) => { if (v && String(v).trim()) q.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`); };
    add('src', opts?.src);
    add('utm_source', opts?.utm_source);
    add('utm_medium', opts?.utm_medium);
    add('utm_campaign', opts?.utm_campaign);
    return q.length ? `?${q.join('&')}` : '';
  })();
  try {
    // Directly use server-supported endpoint
    const resp = await post<any>(`/api/users/${encodeURIComponent(userId)}/analytics/increment${qp}`, json({ metric }), { soft: true });
    return resp.data || { ok: resp.ok };
  } catch {
    return { ok: false };
  }
}

export async function getSystemStats() {
  const res = await get<any>('/api/system-stats', { soft: true });
  return res.data || null;
}

// Global analytics summary over a time range (server computes windowed totals from VisitEvent)
export async function getGlobalSummary(range: string = '7days') {
  const qp = range ? `?range=${encodeURIComponent(range)}` : '';
  const res = await get<any>(`/api/analytics/summary${qp}`, { soft: true });
  return res.data || null;
}

export interface MetricsResult { [k: string]: any }
export async function getDevMetrics(bearer?: string | null) {
  const headers: any = bearer ? { Authorization: `Bearer ${bearer}` } : undefined;
  const res = await get<MetricsResult>('/api/dev/metrics', { soft: true, headers });
  return res.data || null;
}

// Fetch per-user analytics summary (persisted totals) with optional range
export async function getUserSummary(userId: string, range: string = '7days') {
  if (!userId) return null as any;
  const qp = `?userId=${encodeURIComponent(userId)}&range=${encodeURIComponent(range)}`;
  const res = await get<any>(`/api/analytics/summary${qp}`, { soft: true });
  return res.data || null;
}

// (duplicate removed) getGlobalSummary is defined above

export type SSEHandlers = Partial<Record<string, (ev: MessageEvent) => void>> & { onAny?: (ev: MessageEvent) => void; ready?: () => void };

export function subscribeEvents(handlers: SSEHandlers = {}) {
  let es: EventSource | null = null;
  let closed = false;
  let reconnectAttempts = 0;
  const maxReconnectDelay = 30000; // 30 seconds max
  const baseDelay = 1000; // 1 second initial

  function connect() {
    if (closed) return;
    try { es = new EventSource('/api/events'); } catch { return; }
    const { onAny, ready, ...specific } = handlers;
    const wrap = (cb?: (e: MessageEvent)=>void) => (ev: MessageEvent) => { try { if (onAny) onAny(ev); if (cb) cb(ev); } catch {} };
    Object.entries(specific).forEach(([k, fn]) => { if (fn) es!.addEventListener(k, wrap(fn)); });
    es!.onopen = () => {
      reconnectAttempts = 0; // Reset on successful connection
      try { if (typeof ready === 'function') ready(); } catch {}
    };
    es!.onmessage = wrap();
    es!.onerror = () => {
      try { if (es) es.close(); } catch {}
      es = null;
      if (!closed) {
        // Exponential backoff with jitter
        const delay = Math.min(baseDelay * Math.pow(2, reconnectAttempts) + Math.random() * 1000, maxReconnectDelay);
        reconnectAttempts++;
        setTimeout(connect, delay);
      }
    };
  }

  connect();
  return { close(){ closed = true; try { if (es) es.close(); } catch {} } };
}
